import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isAiXstateMigrationEnabled } from '@atlaskit/editor-common/ai-xstate-migration';
import { logException } from '@atlaskit/editor-common/monitoring/error';
import { areNodesEqualIgnoreAttrs, isEmptyDocument } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type {
	DetectionLayer,
	OutcomeClass,
	SessionAbortReason,
} from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import type { CellKindConversionIntent } from '@atlassian/editor-ai-common/utils/normaliseEvents';
import { normaliseAIExperienceEvent } from '@atlassian/editor-ai-common/utils/normaliseEvents';
import type { CommitRejection } from '@atlassian/editor-plugin-ai/commit-outcome';
import { UNSTABLE_configItems } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { EditorPluginAIActions } from '@atlassian/editor-plugin-ai/editor-actions/types';

import type {
	AbortSessionOptions,
	API,
	StreamingApplyMode,
	StreamingCommand,
} from '../aiStreamingOrchestratorPluginType';
import type { InitOptions, QueuedStreamCommand } from '../types/StreamCommandQueue';
import {
	checkAndFireNoDocumentChangeAnalytics,
	createFireADFStreamingAnalyticsEvent,
	fireAttributeOnlyReplaceNodeAnalytics,
	firePacerCompletionGapAnalytics,
	fireSessionOutcomeAnalytics,
	fireSessionStartedAnalytics,
	fireToolCallResult,
	fireToolCallCapturedAnalytics,
	fireCommandError,
	type ToolCallAnomalySymptom,
} from './analytics';
import { createStreamingCommands } from './index';
import {
	analyzeOutcome,
	type DocLike,
	type OutcomeAnalysis,
	type ToolCallRecord,
} from './post-apply-outcome-analyzer';
import { createReapplyCommand, resolveResumePositions } from './reapply';
import { classifyApplyError } from './utils/classify-apply-error';
import { extractExplicitRemovalTargets } from './utils/extract-explicit-removal-targets';
import {
	collectAllLocalIds,
	findNodeByLocalIdAndApi,
	findNodeByLocalIdAndDoc,
} from './utils/find-node-by-local-id';
import { getParentNodeTypes } from './utils/get-parent-node-types';
import { getPmDoc } from './utils/get-pm-doc';
import { compareReplacementStructure } from './utils/has-replacement-structure-mismatch';
import { isAStreamableCommand } from './utils/is-a-streamable-command';
import { makeDocumentValid } from './utils/make-document-valid';
import { mergeStreamCommands } from './utils/merge-stream-commands';
import { createTimeoutWrapper } from './utils/timeout-wrapper';
import { updateInsertAfterDoc } from './utils/update-insert-after-doc';

type EditorPluginAIEvent = Parameters<EditorPluginAIActions['dispatchAIEvent']>[0];

type CommitResultWaitOutcome = 'reported' | 'terminal_error' | 'timed_out' | 'lifecycle_cancelled';

/**
 * Returns the wire-name of the streaming command associated with a failed
 * apply attempt, suitable for the `commandType` attribute on
 * `tool_call_apply_failed` events.
 *
 * Prefers the active `StreamingCommand` (e.g. `'replaceNode'`,
 * `'appendNodeToEnd'`) when available, falling back to the queue-level
 * discriminant (`'streamCommand'`, `'endStream'`, `'initStream'`) and finally
 * to `'unknown'`.
 */
function extractCommandType(
	queuedCommand: QueuedStreamCommand | null,
	activeCommand: StreamingCommand | undefined,
): string {
	if (queuedCommand?.type === 'initStream') {
		return queuedCommand.command.type;
	}
	if (activeCommand?.type) {
		return activeCommand.type;
	}
	return queuedCommand?.type ?? 'unknown';
}

/**
 * Returns the `localId` of the node targeted by a failed apply attempt, if
 * one can be derived from the active command. Returns `undefined` for
 * commands that operate without a target node (e.g. `appendNodeToEnd`,
 * `getPageContent`) or when no command is in flight.
 */
function extractTargetLocalId(
	queuedCommand: QueuedStreamCommand | null,
	activeCommand: StreamingCommand | undefined,
): string | undefined {
	const command = queuedCommand?.type === 'initStream' ? queuedCommand.command : activeCommand;
	if (command && 'localId' in command && typeof command.localId === 'string') {
		return command.localId;
	}
	return undefined;
}

/**
 * Command types whose execution mutates the document. Used by the post-apply
 * outcome analyzer to decide whether `noMeaningfulEffect` should fire — a
 * session that ran nothing but read-only commands cannot be flagged for "no
 * meaningful effect" because no effect was ever requested.
 */
const MUTATING_COMMAND_TYPES: ReadonlySet<string> = new Set([
	'append',
	'appendNode',
	'appendNodeToEnd',
	'replaceNode',
	'removeNode',
	'removeNodes',
	'setAttrs',
	'wrap',
	'wrapNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'moveNode',
]);

/**
 * Commands that anchor the stream to a position inside the existing document by setting the
 * selection when they are applied. For these, the selection's ancestry at apply time is the
 * destination the streamed content will land in, so it is worth recording. Every other
 * command either rewrites the whole document or does not stream content at all.
 */
const SELECTION_ANCHORED_COMMAND_TYPES: ReadonlySet<string> = new Set([
	'replaceNode',
	'insertNodeAfter',
	'insertNodeBefore',
	'appendNode',
	'appendNodeToEnd',
]);

function resolveApplyMode(options?: InitOptions): StreamingApplyMode {
	return options?.applyMode ?? 'stream';
}

/**
 * Returns the `localId` field for a streaming command that targets a
 * single existing node, or `undefined` for commands without one
 * (`appendNode`, `appendNodeToEnd`) or whose target uses a different
 * shape (`removeNodes` uses `localIds[]` plural, not a single `localId`).
 *
 * Used by the pre-apply target-resolution check so we only fire
 * `local_id_not_found` when the command actually expected a node
 * by ID.
 */
function getTargetLocalId(command: StreamingCommand): string | undefined {
	if ('localId' in command && typeof command.localId === 'string') {
		return command.localId;
	}
	return undefined;
}

/**
 * Convert a `StreamingCommand` into the condensed `ToolCallRecord` shape
 * consumed by `analyzeOutcome`. Captures the command type and any localId
 * targets (single or list-shaped). Used to build the per-session tool-call
 * record so the analyzer can distinguish intentional removals/modifications
 * from collateral damage.
 */
function toToolCallRecord(command: StreamingCommand): ToolCallRecord {
	const targetLocalId = getTargetLocalId(command);
	const targetLocalIds =
		'localIds' in command && Array.isArray((command as { localIds?: unknown }).localIds)
			? ((command as { localIds: string[] }).localIds.filter(
					(id): id is string => typeof id === 'string',
				) as string[])
			: undefined;
	return {
		commandType: command.type,
		...(targetLocalId !== undefined ? { targetLocalId } : {}),
		...(targetLocalIds !== undefined && targetLocalIds.length > 0 ? { targetLocalIds } : {}),
	};
}

export class StreamCommandQueue {
	private queue: QueuedStreamCommand[] = [];
	private adfStreamer: ADFStreamer | undefined;
	private streamingCommands: ReturnType<typeof createStreamingCommands>;
	// Resume-mode selection setter used by reapplyCommand().
	private reapply: ReturnType<typeof createReapplyCommand>;
	private currentCommand: StreamingCommand | undefined = undefined;
	/**
	 * Ancestry of the position the current command anchored the stream to, outermost first.
	 * Handed to the ADF streamer so container transforms judge streamed content against its
	 * real destination instead of the synthetic top node it is parsed into.
	 */
	private currentParentNodes: string[] | undefined = undefined;
	private docBeforeStreaming: PMNode | undefined = undefined;
	private isStreaming = false;
	private isFinalStream = false;
	/**
	 * Commit apply keeps chrome up until `endSession`. Set on the first
	 * commit init so session-end can tear down after `currentAction` is cleared.
	 */
	private hasCommitApplyInSession = false;
	private aborted: string[] = [];
	private subscription: (() => void) | null = null;
	private previousValidDoc: { adfDoc?: DocNode; doc?: PMNode } | null = null;
	private endedActionIds: string[] = [];
	private currentAction: {
		applyMode?: StreamingApplyMode;
		id: string;
		initialNode: PMNode | undefined;
		isCreateAction: boolean;
		type: StreamingCommand['type'];
	} | null = null;
	private insertAfterQueueLocalId: string | undefined = undefined;
	/**
	 * The traceId received in response header from AI api call
	 * This is used to trace the AI experience through the backend to frontend.
	 */
	private traceId: string | undefined = undefined;
	/**
	 * The singleInstrumentationId for tracking the AI experience
	 * This is used to correlate events across multiple stream processing passes
	 */
	private singleInstrumentationId: string | undefined = undefined;
	/**
	 * The promptVersion from action metadata
	 * This is used to track which version of the prompt was used for analytics
	 */
	private promptVersion: string | undefined = undefined;
	/**
	 * Aggregated repair signals across every streaming action in the current
	 * session, used to fire the authoritative `sessionOutcome` event exactly
	 * once per session.
	 *
	 * `*Invalid` aggregates with OR (any action that observed an invalid layer
	 * marks the session as having seen an invalid layer).
	 *
	 * `*UnrepairedCount` counts how many actions had an invalid layer that was
	 * NOT recovered. From these we derive a correct session-level "repaired"
	 * value as: `*Invalid && *UnrepairedCount === 0` — i.e. every invalid
	 * occurrence in the session was recovered.
	 *
	 * This avoids a misleading `success_with_repairs` classification when an
	 * earlier action repaired successfully but a later action failed to repair:
	 * a naive OR over `*Repaired` would incorrectly flatten the irrecoverable
	 * failure into a success.
	 */
	private sessionRepairSignals: {
		anyAdfInvalid: boolean;
		anyJsonInvalid: boolean;
		unrepairedAdfCount: number;
		unrepairedJsonCount: number;
	} = {
		anyJsonInvalid: false,
		anyAdfInvalid: false,
		unrepairedJsonCount: 0,
		unrepairedAdfCount: 0,
	};
	/**
	 * Tracks whether the `sessionOutcome` analytics event has already been fired
	 * for the active session. Ensures the event fires at most once per session.
	 */
	private sessionOutcomeFired = false;
	private sessionAnomalyCounts: Partial<Record<ToolCallAnomalySymptom, number>> = {};
	/** Cleanup handle for the deferred no_document_change analytics subscription. Cleared in abort/destroy. */
	private pendingNoDocChangeUnsubscribe: (() => void) | undefined = undefined;
	private pendingCommitResultCancel: ((reason?: 'timeout' | 'lifecycle') => void) | undefined;
	/**
	 * Set to true when a toolCallResult event has already been fired for the current
	 * command (e.g. apply_failed_adf_schema_error from repair signals). Prevents
	 * scheduleDeferredNoDocChangeAnalytics from firing a second toolCallResult for
	 * the same command.
	 */
	private toolCallResultFiredForCurrentCommand = false;
	/** Distinguishes deferred callbacks captured for different commands. */
	private currentCommandResultId = 0;
	private finalRequestedReplacement: string | undefined;
	private cellKindConversionIntent: CellKindConversionIntent | undefined;
	private sessionStructuralLoss = false;
	private sessionCellConversionRejected = false;
	private sessionCommitRejection: CommitRejection | undefined;

	private shouldObserveCommitOutcomes(): boolean {
		return (
			isAiXstateMigrationEnabled() &&
			this.api.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState
				?.isInDocumentStreamingEnabled === true &&
			isExperimentEnabled('platform_editor_ai_commit_rejection')
		);
	}

	private shouldVerifyStructure(): boolean {
		return (
			this.telemetryEventsEnabled &&
			isAiXstateMigrationEnabled() &&
			fg('platform_editor_ai_structural_loss_telemetry')
		);
	}

	/**
	 * Per-session record of every tool call processed by `applyCommand`.
	 * Consumed at end-of-session by `analyzeOutcome` to compute
	 * `affectedLocalIds`. Reset at all session boundaries.
	 */
	private sessionToolCalls: ToolCallRecord[] = [];

	/**
	 * The most recent `analyzeOutcome` result, computed at end-of-session.
	 * Read by `fireSessionOutcomeOnce` (added by CCI-15893) to enrich the
	 * `sessionOutcome` event with structural-delta fields. Undefined until
	 * the first end-of-session analysis runs in the active lifetime, and
	 * reset at all session boundaries.
	 */
	private lastOutcomeAnalysis: OutcomeAnalysis | undefined = undefined;

	/**
	 * Snapshot of the editor document at session start, captured by
	 * `startSession`. Consumed at end-of-session by `analyzeOutcome` as
	 * `preSessionAdf`. Distinct from `docBeforeStreaming`, which is
	 * reassigned per command for the per-command repair-signal pipeline.
	 * Reset at all session boundaries.
	 */
	private preSessionDoc: PMNode | undefined = undefined;

	constructor(
		private readonly api: API,
		private readonly streamFlowPacer: StreamFlowPacer | null = null,
		private readonly telemetryEventsEnabled: boolean = false,
	) {
		this.streamingCommands = createStreamingCommands(api);
		this.reapply = createReapplyCommand(api);
	}

	private isActorReady(): boolean {
		try {
			const currentState = this.api.aiExperience.sharedState.currentState();
			if (!isAiXstateMigrationEnabled()) {
				// Check if actor exists and is in streaming state
				const actor = this.api.aiExperience.actions.getAiExperienceActor();
				return actor && currentState?.isStreaming === true;
			}
			return currentState?.isStreaming === true;
		} catch {
			return false;
		}
	}

	private commandToProcess(commandToProcess: QueuedStreamCommand): Promise<void> {
		const { type, actionId, chunk } = commandToProcess;

		if (type === 'initStream') {
			return this.initStreamPromise(commandToProcess.command, commandToProcess.options);
		} else if (type === 'endStream') {
			return this.endExperiencePromise(commandToProcess);
		} else if (commandToProcess.isComplete !== undefined) {
			// Branching here (rather than always calling with 4 args, which is behaviourally
			// identical in processStreamCommand) deliberately preserves the exact 2-argument
			// call shape when isComplete is absent. Several existing specs (e.g.
			// mergeStreamCommands.spec.ts) assert `processStreamCommandPromise` is called with
			// an exact argument count via `toHaveBeenNthCalledWith`; always passing a 4th
			// (possibly `undefined`) argument breaks those pre-existing assertions.
			return this.processStreamCommandPromise(actionId, chunk, false, commandToProcess.isComplete);
		} else {
			return this.processStreamCommandPromise(actionId, chunk);
		}
	}

	private processingQueue = false;
	private inProcessing: string[] = [];
	// Id of a timeout-force-finished tool call being processed; read once when
	// emitting the terminal `complete` event so it rides to `buildCommitTransaction`.
	private pendingForceAbortedToolCallId: string | undefined = undefined;
	private queueTimeout: NodeJS.Timeout | null = null;
	// This is only used *if* we have started receiving values with a different
	// action id (new stream) and we have nothing left in the current stream (ie. it hasn't finished)
	// Really this shouldn't be hit since this would only happen on the edge when we're wrapping up the old stream
	// and starting the next early
	private CURRENT_QUEUE_TIMEOUT = 5000;
	private currentCommandTimeoutReset: (() => void) | null = null;

	private emitToolCallResult(
		args: Parameters<typeof fireToolCallResult>[0],
		sessionAnomalyCounts: Partial<Record<ToolCallAnomalySymptom, number>> = this
			.sessionAnomalyCounts,
	): void {
		if (
			args.result === 'anomaly' &&
			isExperimentEnabled('platform_editor_ai_session_anomaly_counts_exp')
		) {
			sessionAnomalyCounts[args.symptom] = (sessionAnomalyCounts[args.symptom] ?? 0) + 1;
		}
		fireToolCallResult(args);
	}

	private async processQueue() {
		if (this.processingQueue) {
			return;
		}
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.processingQueue = true;
		let commandsToProcess = [...this.queue].filter(
			(cmd) => cmd.actionId === this.currentAction?.id,
		);
		this.inProcessing = [...new Set(commandsToProcess.map((cmd) => cmd.actionId))];
		this.queue = this.queue.filter((cmd) => !commandsToProcess.includes(cmd));
		let currentCommand = null;

		try {
			commandsToProcess = mergeStreamCommands(commandsToProcess);
			for (const commandToProcess of commandsToProcess) {
				currentCommand = commandToProcess;
				if (this.aborted.includes(commandToProcess.actionId)) {
					// If we abort the last stream let's move onto the next stream
					if (commandToProcess.type === 'endStream') {
						this.resolvePendingQueue(true);
					}
					continue;
				}
				await this.wrapInTimeout(this.commandToProcess(commandToProcess), commandToProcess);
			}
		} catch (error) {
			fireCommandError({
				api: this.api,
				traceId: this.traceId,
				command: currentCommand,
				isCreateAction: this.currentAction?.isCreateAction,
				error,
			});
			// Fire the unified tool_call_anomaly event in parallel —
			// keeps the existing fireCommandError event flowing for
			// legacy dashboards while attribution-first dashboards
			// consume the new event.
			if (this.telemetryEventsEnabled) {
				const { symptom } = classifyApplyError(error);
				this.emitToolCallResult({
					api: this.api,
					traceId: this.traceId,
					commandType: extractCommandType(currentCommand, this.currentCommand),
					targetLocalId: extractTargetLocalId(currentCommand, this.currentCommand),
					errorMessage: ErrorUtils.extractErrorMessage(error),
					result: 'anomaly',
					symptom,
				});
			}
		} finally {
			// Reset the processing queue flag
			this.processingQueue = false;
			const lastCommand = commandsToProcess.pop();
			this.inProcessing = [];
			if (lastCommand?.type === 'endStream') {
				this.checkAndResolveEmptyQueue();
			}
		}
		// If commands have been added to the queue during async operations, process them now
		if (this.queue.filter((cmd) => cmd.actionId === this.currentAction?.id).length > 0) {
			this.processQueue();
		} else if (this.queue.length > 0) {
			this.queueTimeout = setTimeout(() => {
				this.resolvePendingQueue(true);
				this.processQueue();
			}, this.CURRENT_QUEUE_TIMEOUT);
		}
	}

	private onEmptyQueueResolve: (() => void) | null = null;
	public onEmptyQueue(): Promise<void> {
		const promise = new Promise<void>((resolve) => {
			this.onEmptyQueueResolve = resolve;
		});
		this.checkAndResolveEmptyQueue();
		return promise;
	}

	// 2 seconds - this is the maximum time to process a command before we abort it and continue
	// This can be longer if for example if a lot of commands have been processed, but the stream flow
	// pacer has a backlog (particularly for elements with lots of attributes / marks etc) and hasn't had
	// a chance to emit yet
	private COMMAND_TIMEOUT = 2000;

	private async wrapInTimeout(promise: Promise<void>, cmd: QueuedStreamCommand): Promise<void> {
		const { promise: wrappedPromise, resetTimeout } = createTimeoutWrapper(promise, {
			timeout: this.COMMAND_TIMEOUT,
			onTimeout: () => {
				if (cmd.type === 'endStream') {
					this.pendingCommitResultCancel?.('timeout');
				}
			},
			onCleanup: () => {
				this.currentCommandTimeoutReset = null;

				if (cmd.type === 'endStream') {
					this.checkAndResolveEmptyQueue();
				}
				this.resolvePendingQueue();
			},
		});

		this.currentCommandTimeoutReset = resetTimeout;
		await wrappedPromise;
	}

	/**
	 * End the AI experience
	 */
	public endStreamCommand(actionId: string, options?: { forceAbortedToolCallId?: string }): void {
		// duplicated endStreamCommand will kill accept reject screen prematurely
		if (this.endedActionIds.includes(actionId)) {
			return;
		}
		this.endedActionIds.push(actionId);
		if (this.endedActionIds.length > 10) {
			this.endedActionIds.shift();
		}

		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({
			type: 'endStream',
			actionId,
			chunk: undefined,
			reason: 'endCurrentStream',
			forceAbortedToolCallId: options?.forceAbortedToolCallId,
		});
		this.processQueue();
	}

	private endExperience() {
		// if it's isFinalStream
		// - when auto accept AI changes is on, editor-plugin-ai will end itself
		// - when auto accept AI changes is off, the accept reject screen will be shown
		// if it's not isFinalStream, the endAIExperience is used to kill a previous AI modal
		// - which can be the previous accept reject screen
		// - or the command palette screen (followed by rovo free-gen screen)
		if (!this.isFinalStream) {
			this.api.aiExperience.actions.endAIExperience();
		}
		this.isFinalStream = false;
		this.adfStreamer?.reset();
		this.streamFlowPacer?.reset();
		this.subscription?.();
	}
	private isPendingResolveQueue = false;

	private resolvePendingQueue(timeout = false) {
		if (timeout || this.isPendingResolveQueue) {
			const currentAction = this.queue.find((cmd) => cmd.type === 'initStream') ?? null;
			if (timeout) {
				// Queue-stall timeout fired (no further commands arrived).
				// Idempotent via `fireSessionOutcomeOnce`'s once-per-session guard.
				this.fireSessionOutcomeOnce({ lifecycleSymptom: 'session_timeout' });
			}
			this.currentAction = currentAction
				? {
						id: currentAction.actionId,
						isCreateAction:
							(currentAction.command.type === 'appendNode' ||
								currentAction.command.type === 'appendNodeToEnd') &&
							currentAction.command.streamingType === 'create',
						initialNode: findNodeByLocalIdAndApi(
							this.api,
							// replaceDoc has no localId — only replaceNode needs one here.
							currentAction.command.type === 'replaceNode' && currentAction.command.localId,
						),
						type: currentAction.command.type,
						applyMode: resolveApplyMode(currentAction.options),
					}
				: null;
			this.isPendingResolveQueue = false;
		}
	}

	private checkAndResolveEmptyQueue() {
		if (this.queue.length === 0 && this.inProcessing.length === 0) {
			this.onEmptyQueueResolve?.();
		}
	}

	private endNonStreamble() {
		if (isAiXstateMigrationEnabled()) {
			this.api.aiExperience.actions.dispatchAIEvent({
				type: 'complete',
				adfResponse: undefined,
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			send({
				type: 'complete',
				markdown: '',
				adfResponse: undefined,
				cacheKey: '',
				modelInput: {},
			});
		}
	}

	private async endExperiencePromise(
		command: Extract<QueuedStreamCommand, { type: 'endStream' }>,
	): Promise<void> {
		const { actionId, reason } = command;
		const capturedAction = this.currentAction;
		// Carry the force-aborted tool call id onto the terminal `complete` event
		// (emitted by finalStream below) so `buildCommitTransaction` can persist its
		// final positions for resume.
		this.pendingForceAbortedToolCallId = command.forceAbortedToolCallId;
		this.isPendingResolveQueue = reason === 'endCurrentStream';
		// should not check empty queue if the end command is triggered by the initStreamCommand
		// which is used to close a previous AI modal
		// because there are more commands to process even if the queue is empty
		// and this.isPendingResolveQueue will be reset, so use a different flag here
		const shouldCheckEmptyQueue = reason === 'endCurrentStream';
		const isStreamingCommand = !(
			this.currentAction?.type && !isAStreamableCommand({ type: this.currentAction?.type })
		);
		const isCommit = this.currentAction?.applyMode === 'commit';
		const cleanupAfterCommitWaitTimeout = () => {
			if (this.currentAction !== capturedAction) {
				return;
			}
			if (isCommit) {
				this.adfStreamer?.reset();
				this.streamFlowPacer?.reset();
				this.subscription?.();
				this.isFinalStream = false;
			} else {
				this.endExperience();
			}
			this.resolvePendingQueue();
			if (shouldCheckEmptyQueue) {
				this.checkAndResolveEmptyQueue();
			}
		};
		// Subscribe before the terminal event is queued below. With concurrent React,
		// the pacer can drain that event while `processStreamCommandPromise` yields;
		// subscribing afterwards lets the next action start before this one has fully
		// committed to the editor.
		const waitForPacerCompletion =
			!isCommit && this.isStreaming && isStreamingCommand && this.streamFlowPacer?.isRunning()
				? new Promise<void>((resolve) => this.streamFlowPacer?.onComplete(resolve))
				: undefined;

		if (this.isStreaming && isStreamingCommand) {
			const observeCommit = this.shouldObserveCommitOutcomes();
			// Commit apply can finish synchronously; subscribe before dispatching COMPLETE.
			const commitResultComplete = observeCommit
				? this.scheduleDeferredNoDocChangeAnalytics(true, cleanupAfterCommitWaitTimeout)
				: undefined;
			await this.processStreamCommandPromise(actionId, '', true);
			if (observeCommit) {
				commitResultComplete?.afterCompleteDispatch();
				const waitOutcome = await commitResultComplete?.completed;
				if (
					waitOutcome === 'timed_out' ||
					waitOutcome === 'lifecycle_cancelled' ||
					this.currentAction !== capturedAction
				) {
					return;
				}
			}
			if (this.currentAction?.type === 'replaceNode') {
				fireAttributeOnlyReplaceNodeAnalytics({
					api: this.api,
					initialNode: this.currentAction.initialNode,
					lastDoc: this.adfStreamer?.getLastDoc(),
				});
			}
			if (!observeCommit) {
				this.scheduleDeferredNoDocChangeAnalytics();
			}
		} else if (!isStreamingCommand) {
			this.isFinalStream = true;
		}
		// Commit apply does not mount typewriter decorations, so do not
		// wait on streaming state. Do not call `endAIExperience` per tool
		// call (loader race / FIFO stall); chrome lives startSession → endSession.
		if (isCommit) {
			this.adfStreamer?.reset();
			this.streamFlowPacer?.reset();
			this.subscription?.();
			this.isFinalStream = false;
			this.resolvePendingQueue();
			if (shouldCheckEmptyQueue) {
				this.checkAndResolveEmptyQueue();
			}
			return;
		}
		return new Promise<void>((resolve) => {
			const endNow = () => {
				this.endExperience();
				// After we end the experience there are a number of React re-renders that occur immediately after
				// This causes the state to flick between streaming and not streaming, it's not pretty
				// but wrap this in a `requestAnimationFrame` to delay our check until this clears.
				// To fix this properly we probably need to decouple the state with React
				const checkStreaming = () => {
					const resolveAndEndStream = () => {
						resolve();
						this.resolvePendingQueue();
						shouldCheckEmptyQueue && this.checkAndResolveEmptyQueue();
					};
					if (!isStreamingCommand) {
						this.endNonStreamble();
						resolveAndEndStream();
						return;
					}
					if (!this.api.aiExperience?.sharedState.currentState()?.isStreaming) {
						resolveAndEndStream();
						return;
					}
					const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
						if (!nextSharedState.isStreaming) {
							unsubscribe();
							resolveAndEndStream();
						}
					});
				};

				setTimeout(checkStreaming, 0);
			};

			if (waitForPacerCompletion) {
				void waitForPacerCompletion.then(() => {
					// Need to be careful not to end the experience too soon. We need to give the xstate and react time to process
					// the final stream events before we kill the experience. Otherwise you will see an unfinished stream in the doc.
					setTimeout(endNow, 100);
				});
			} else {
				// If we're not running the pacer or its a non-streamable command, end immediately
				endNow();
			}
		});
	}

	/**
	 * Streams the AI experience
	 */
	/**
	 * Called by the orchestrator plugin when the chunk strategy detects that
	 * ADF schema content was rejected (e.g. blockCard inside listItem).
	 * Fires a toolCallResult anomaly and suppresses the deferred success event.
	 */
	public notifySchemaContentRejection(actionId: string, errorMessage?: string): void {
		if (this.toolCallResultFiredForCurrentCommand) {
			return;
		}
		const command = this.currentCommand;
		const traceId = this.traceId;
		if (!command || !traceId) {
			return;
		}
		this.toolCallResultFiredForCurrentCommand = true;
		this.emitToolCallResult({
			api: this.api,
			traceId,
			commandType: command.type,
			result: 'anomaly',
			symptom: 'apply_failed_adf_schema_error',
			errorMessage,
		});
	}

	/**
	 * Called by setAttrs when the model supplied an attribute key that does
	 * not belong on the target node's spec. Fires an
	 * `attrs_invalid_for_node_type` anomaly and suppresses the deferred
	 * `no_document_change` check for the same command, so one failed setAttrs
	 * call produces exactly one toolCallResult event, not two.
	 */
	public notifyInvalidAttrs(
		nodeType?: string,
		invalidAttrKey?: string,
		errorMessage?: string,
	): void {
		if (this.toolCallResultFiredForCurrentCommand) {
			return;
		}
		const command = this.currentCommand;
		const traceId = this.traceId;
		if (!command || !traceId) {
			return;
		}
		// Respect the same telemetry opt-out as every other apply-time
		// diagnostic in this class (e.g. no_document_change, local_id_not_found).
		if (!this.telemetryEventsEnabled) {
			return;
		}
		this.toolCallResultFiredForCurrentCommand = true;
		this.emitToolCallResult({
			api: this.api,
			traceId,
			commandType: command.type,
			result: 'anomaly',
			symptom: 'attrs_invalid_for_node_type',
			nodeType,
			invalidAttrKey,
			errorMessage,
		});
	}

	public queueStreamCommand(
		actionId: string,
		chunk: string,
		options?: { isComplete?: boolean },
	): void {
		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({
			type: 'streamCommand',
			actionId,
			chunk,
			isComplete: options?.isComplete,
		});
		// Commit apply never flips typewriter `isStreaming`, so `isActorReady`
		// stays false. Still drain — finish/endSession wait on an empty queue.
		if (this.isActorReady() || this.currentAction?.applyMode === 'commit') {
			this.processQueue();
		} else {
			// if there are multiple sequential queueStreamCommand calls
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api?.aiExperience.sharedState.onChange(() => {
				if (this.isActorReady()) {
					unsubscribe?.();
					this.processQueue();
				}
			});
		}
	}

	private hasChangedDocument(latestDoc: ReturnType<typeof makeDocumentValid>): boolean {
		if (!this.previousValidDoc && latestDoc) {
			return true;
		}

		const nodesAreNotEqual =
			!this.previousValidDoc?.doc ||
			!latestDoc?.doc ||
			!areNodesEqualIgnoreAttrs(this.previousValidDoc?.doc, latestDoc.doc);
		if (this.streamFlowPacer === null) {
			// replaceDoc is allowed to produce an empty document (e.g. "delete entire page"),
			// so we skip the isEmptyDocument guard for that action type.
			const isReplaceDoc = this.currentAction?.type === 'replaceDoc';
			if (isReplaceDoc) {
				// For replaceDoc, latestDoc.doc may be undefined when the streamed content is
				// empty HTML (the adfStreamer returns undefined for empty input). We treat
				// "undefined → previous doc exists" as a change so the empty-page intent
				// is honoured — the final stream path will supply the empty-paragraph fallback.
				return Boolean(nodesAreNotEqual);
			}
			return Boolean(nodesAreNotEqual && latestDoc.doc && !isEmptyDocument(latestDoc.doc));
		}
		const latestCount = this.streamFlowPacer?.adfTextLen(latestDoc?.adfDoc);
		return Boolean(
			nodesAreNotEqual &&
			latestDoc.adfDoc &&
			this.previousValidDoc?.adfDoc &&
			latestCount.chars > 0 &&
			// Ensure we only stream changes if the stream flow pacer detects semantic changes
			!this.streamFlowPacer?.adfTextLen(this.previousValidDoc?.adfDoc).gte(latestCount),
		);
	}

	private createCallback(usePush: boolean) {
		if (usePush) {
			return (evt: EditorPluginAIEvent) => {
				const fullEvent = normaliseAIExperienceEvent(evt);
				this?.streamFlowPacer?.push(fullEvent);
			};
		}

		if (isAiXstateMigrationEnabled()) {
			return (evt: EditorPluginAIEvent) => {
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			};
		}

		// Legacy path: send to actor with padded defaults
		const { send } = this.api.aiExperience.actions.getAiExperienceActor();
		return (evt: EditorPluginAIEvent) => {
			const payload = normaliseAIExperienceEvent(evt);
			send(payload as Parameters<typeof send>[0]);
		};
	}

	private processStreamCommand(
		actionId: string,
		chunk: string,
		finalStream = false,
		isComplete?: boolean,
	) {
		if (isAiXstateMigrationEnabled()) {
			this.streamFlowPacer?.setEmit((evt: EditorPluginAIEvent) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			this.streamFlowPacer?.setEmit((evt: Parameters<typeof send>[0]) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				send(evt);
			});
		}

		const isCommit = this.currentAction?.applyMode === 'commit';
		const callback = this.createCallback(!!this.streamFlowPacer?.push && !isCommit);

		// Initialize streamer if needed
		if (!this.adfStreamer) {
			const schema = this.api.core.sharedState.currentState()?.schema;
			this.adfStreamer = schema
				? new ADFStreamer(
						schema,
						createFireADFStreamingAnalyticsEvent({
							api: this.api,
							isCreateAction: this.currentAction?.isCreateAction ?? false,
							traceId: this.traceId,
							promptVersion: this.promptVersion,
						}),
					)
				: undefined;
		}

		// Set on every chunk (including back to `undefined`) so a destination recorded for an
		// earlier command can never leak into a later one.
		this.adfStreamer?.setParentNodes(this.currentParentNodes);

		if (finalStream) {
			const lastDocResult = makeDocumentValid(this.api, this.adfStreamer?.getLastDoc());
			let adfResponseBefore = lastDocResult.adfDoc;

			// replaceDoc with an empty value (e.g. "delete entire page") produces either no
			// ADF or a bare { type:"doc" } with no content from the streamer. Substitute a
			// minimal empty document so the downstream insertion path actually clears the
			// editor content rather than silently no-oping.
			const isFinalEffectivelyEmptyDoc =
				!adfResponseBefore || !adfResponseBefore.content || adfResponseBefore.content.length === 0;
			if (isFinalEffectivelyEmptyDoc && this.currentAction?.type === 'replaceDoc') {
				adfResponseBefore = {
					version: 1,
					type: 'doc',
					content: [{ type: 'paragraph', content: [] }],
				} as DocNode;
			}

			const schema = this.api.core.sharedState.currentState()?.schema;
			const { updatedResponse, localId } = updateInsertAfterDoc(
				adfResponseBefore,
				this.currentAction?.type,
				schema,
			);
			const adfResponse = updatedResponse;
			this.insertAfterQueueLocalId = localId;

			const repairSignals = this.adfStreamer?.endStreamingSession({
				hasProcessedFinalResponse: true,
			});
			if (repairSignals) {
				// A session may contain multiple streaming actions. Aggregate
				// repair signals across them so the final `sessionOutcome` event
				// correctly reflects the worst per-action outcome:
				// - `*Invalid`: OR — any invalid action means the session saw
				//   that layer fail at least once.
				// - `unrepaired*Count`: count actions whose invalid layer was
				//   NOT recovered. The session is only considered "fully
				//   repaired" if this count is zero. A naive OR over the
				//   per-action `*Repaired` flags would incorrectly mask a
				//   later irrecoverable failure when an earlier action had
				//   recovered.
				this.sessionRepairSignals = {
					anyJsonInvalid:
						this.sessionRepairSignals.anyJsonInvalid || repairSignals.finalJsonInvalid,
					anyAdfInvalid: this.sessionRepairSignals.anyAdfInvalid || repairSignals.finalAdfInvalid,
					unrepairedJsonCount:
						this.sessionRepairSignals.unrepairedJsonCount +
						(repairSignals.finalJsonInvalid && !repairSignals.finalJsonRepaired ? 1 : 0),
					unrepairedAdfCount:
						this.sessionRepairSignals.unrepairedAdfCount +
						(repairSignals.finalAdfInvalid && !repairSignals.finalAdfRepaired ? 1 : 0),
				};
			}
			const forceAbortedToolCallId = this.pendingForceAbortedToolCallId;
			this.pendingForceAbortedToolCallId = undefined;
			callback({
				type: 'complete',
				...(this.shouldObserveCommitOutcomes() && { actionId }),
				adfResponse,
				...(this.cellKindConversionIntent && {
					cellKindConversionIntent: this.cellKindConversionIntent,
				}),
				...(forceAbortedToolCallId ? { forceAbortedToolCallId } : {}),
			});
			return true;
		}

		if (this.currentCommand?.type === 'replaceNode' && this.shouldVerifyStructure()) {
			this.finalRequestedReplacement = chunk;
		}

		const targetRole = this.currentAction?.initialNode?.type.spec.tableRole;
		if (
			this.currentCommand?.type === 'replaceNode' &&
			(targetRole === 'cell' || targetRole === 'header_cell') &&
			isExperimentEnabled('platform_editor_ai_replace_cell_kind')
		) {
			// Retain attribute presence before the streamer repairs and materializes defaults.
			let rawAdf: unknown;
			try {
				rawAdf = JSON.parse(chunk);
			} catch {
				// An incomplete source cannot authorize an atomic wrapper conversion.
			}
			this.cellKindConversionIntent = { adf: rawAdf };
		} else {
			this.cellKindConversionIntent = undefined;
		}

		// Resetting because we are going to get full ADF as chunk
		this.adfStreamer?.reset();
		this.adfStreamer?.processChunk(chunk);

		// Normally the streamer withholds the document's last leaf node because its content
		// may still be partial (e.g. a link whose href is mid-stream). When the caller tells us
		// this chunk's accumulated value is already a fully-closed document, render the last
		// leaf immediately instead of waiting for the next chunk or stream completion — without
		// this, a last-leaf link can sit invisible until the whole stream (and any trailing
		// remix-creation step) finishes. See createCommandProcessor's `isComplete` call site.
		const shouldFlushLastLeaf =
			isExperimentEnabled('platform_editor_ai_create_stream_flush_last_leaf') && isComplete;
		const latestDoc = makeDocumentValid(
			this.api,
			isCommit || shouldFlushLastLeaf
				? this.adfStreamer?.getLastDoc()
				: this.adfStreamer?.getLastDocWithoutLastLeafNode(),
		);

		const changed = this.hasChangedDocument(latestDoc);
		this.previousValidDoc = latestDoc;

		// We compare the before/after docs because if nothing has changed then it will not trigger a react update (because the adfResponse needs to be different)
		// which means the chunkNumber will never update and the processStreamCommandPromise Promise will never resolve.
		// This cause the wrapInTimeout 1sec delay to occur before the next chunk is processed. This means we can end up in a situation
		// where we're recieving a lot of chunks, but nothing in the view is changed and everything starts taking a lot of time
		// because of the sequential 1sec failovers.
		if (changed) {
			// For replaceDoc with an empty value (e.g. "delete entire page"), the adfStreamer
			// returns either undefined or a bare { type:"doc" } with no content — fall back to
			// a minimal empty document so the editor content is actually cleared.
			const isEffectivelyEmptyDoc =
				!latestDoc.adfDoc || !latestDoc.adfDoc.content || latestDoc.adfDoc.content.length === 0;
			const streamAdfResponse =
				isEffectivelyEmptyDoc && this.currentAction?.type === 'replaceDoc'
					? ({
							version: 1,
							type: 'doc',
							content: [{ type: 'paragraph', content: [] }],
						} as DocNode)
					: latestDoc.adfDoc;
			callback({
				type: 'stream',
				adfResponse: streamAdfResponse,
				...(this.cellKindConversionIntent && {
					cellKindConversionIntent: this.cellKindConversionIntent,
				}),
			});
			return true;
		}

		return false;
	}

	private processStreamCommandPromise(
		actionId: string,
		chunk: string,
		finalStream = false,
		isComplete?: boolean,
	): Promise<void> {
		this.isStreaming = !finalStream;
		this.isFinalStream = finalStream;
		return new Promise<void>((resolve) => {
			// do not wrap the code in setTimeout, it will pause the streaming when the tab is in background
			const chunkBefore = this.api.aiExperience?.sharedState.currentState()?.chunkNumber;
			const processed = this.processStreamCommand(actionId, chunk, finalStream, isComplete);
			// Commit writes without typewriter, so `chunkNumber` may never change.
			if (!processed || this.currentAction?.applyMode === 'commit') {
				// If nothing was processed then we should be able to end immediately since we will never get a chuckNumber update
				// this means the next chunk can be processed straight away.
				resolve();
				return;
			}

			if (chunkBefore !== this.api.aiExperience?.sharedState.currentState()?.chunkNumber) {
				resolve();
				return;
			}
			// if there are multiple sequential queueStreamCommand calls and one of them timeout
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(
				({ nextSharedState, prevSharedState }) => {
					if (nextSharedState?.chunkNumber !== prevSharedState?.chunkNumber) {
						unsubscribe?.();
						resolve();
					}
				},
			);
		});
	}

	/**
	 * Streams the AI experience
	 */
	public initStreamCommand(
		actionId: string,
		command: StreamingCommand,
		options?: InitOptions,
	): void {
		this.isAborted = false;

		// Track tool call captured for analytics
		fireToolCallCapturedAnalytics({
			api: this.api,
			command,
			actionId,
			traceId: this.traceId,
		});
		if (!this.currentAction) {
			this.currentAction = {
				id: actionId,
				isCreateAction:
					(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
					command.streamingType === 'create',
				initialNode: findNodeByLocalIdAndApi(
					this.api,
					// replaceDoc has no localId — only pass localId for replaceNode.
					command.type === 'replaceNode' && command.localId,
				),
				type: command.type,
				applyMode: resolveApplyMode(options),
			};
		}
		if (resolveApplyMode(options) === 'commit') {
			this.hasCommitApplyInSession = true;
		}
		// If we're already running end the stream and restart.
		// Commit chrome stays up until endSession — do not insert a
		// teardown between FIFO tool calls.
		if (
			this.currentAction?.applyMode !== 'commit' &&
			this.api.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState.isRunning
		) {
			this.queue.push({
				type: 'endStream',
				actionId,
				chunk: undefined,
				reason: 'endPreviousStream',
			});
		}
		this.queue.push({
			type: 'initStream',
			actionId,
			chunk: undefined,
			command,
			options,
		});
		this.processQueue();
	}

	private applyCommand(command: StreamingCommand) {
		const insertAfterQueue =
			this.currentCommand?.type === 'insertNodeAfter' &&
			command.type === 'insertNodeAfter' &&
			command.localId === this.currentCommand?.localId;

		this.currentCommand = command;
		this.currentCommandResultId += 1;
		this.toolCallResultFiredForCurrentCommand = false;
		this.finalRequestedReplacement = undefined;
		this.cellKindConversionIntent = undefined;
		this.docBeforeStreaming = getPmDoc(this.api);
		let isSuccessful = true;

		// Apply-anomaly diagnostics: see runPre/PostApplyDiagnostics for
		// the symptoms detected.
		// Record this tool call so end-of-session `analyzeOutcome` can
		// distinguish intentional removals/modifications from collateral
		// damage.
		this.sessionToolCalls.push(toToolCallRecord(command));
		// The localId the apply path will actually attempt to act on.
		// `insertAfterQueue` commands rewrite the localId at apply time
		// so that consecutive `insertNodeAfter` calls chain correctly
		// (see the apply branch below). Diagnostics must use the same
		// effective localId so target-resolution checks match the apply
		// path's actual behaviour — otherwise we get false positives
		// (command.localId missing but insertAfterQueueLocalId valid) and
		// false negatives (the inverse).
		const effectiveTargetLocalId =
			insertAfterQueue && this.insertAfterQueueLocalId
				? this.insertAfterQueueLocalId
				: getTargetLocalId(command);
		const preApplyResult = this.runPreApplyDiagnostics(command, effectiveTargetLocalId);

		// If we have subsequent "insertAfter" commands with the same localId, instead point to the end of the
		// previous insertion. This is because the AI will "insertAfter" in a logical order, but that puts the content
		// into the document backwards.
		if (insertAfterQueue && this.insertAfterQueueLocalId) {
			// If for some reason the localId doesn't exist in the document fallback to the original stream command
			try {
				isSuccessful = this.streamingCommands[command.type]({
					...command,
					localId: this.insertAfterQueueLocalId,
				});
			} catch {
				isSuccessful = this.streamingCommands[command.type](command);
			}
		} else {
			isSuccessful = this.streamingCommands[command.type](command);
		}

		const postApplyAnomalyDetected = !preApplyResult.anomalyDetected
			? this.runPostApplyDiagnostics(
					command,
					preApplyResult.preApplyLocalIds,
					effectiveTargetLocalId,
				)
			: false;

		// Fire success only if no anomaly detected and not a streamable
		// command. Streamable commands defer success/anomaly to
		// checkAndFireNoDocumentChangeAnalytics once streaming completes, since
		// the doc change arrives via chunks after applyCommand returns.
		if (this.telemetryEventsEnabled) {
			const preApplyAnomalyDetected = preApplyResult.anomalyDetected;
			if (
				!preApplyAnomalyDetected &&
				!postApplyAnomalyDetected &&
				!isAStreamableCommand(command) &&
				// Skip if the command already fired its own anomaly directly (e.g.
				// setAttrs via notifyInvalidAttrs) — one command should produce
				// exactly one toolCallResult event, not an anomaly plus this success.
				!this.toolCallResultFiredForCurrentCommand
			) {
				this.emitToolCallResult({
					api: this.api,
					traceId: this.traceId,
					commandType: command.type,
					result: 'success',
				});
			}
		}

		return isSuccessful;
	}

	/**
	 * Apply-anomaly diagnostics: pre-apply phase. Snapshots full-depth
	 * localIds and fires `local_id_not_found` if the command targets
	 * a localId that doesn't exist. Returns the snapshot for reuse by the
	 * post-apply phase.
	 *
	 * Caller is responsible for computing `effectiveTargetLocalId` so it
	 * reflects what the apply path will actually act on (e.g. the substituted
	 * `insertAfterQueueLocalId` for chained `insertNodeAfter` calls).
	 */
	private runPreApplyDiagnostics(
		command: StreamingCommand,
		effectiveTargetLocalId: string | undefined,
	): { anomalyDetected: boolean; preApplyLocalIds: Set<string> } {
		// Only the retired `unexpected_node_removal` diff consumes this, so skip
		// the full-depth walk once the gate is on.
		const preApplyLocalIds = fg('platform_editor_ai_remove_node_removal_symptom')
			? new Set<string>()
			: collectAllLocalIds(this.docBeforeStreaming ?? undefined);

		// Use the live PM doc lookup (same walk every command uses) so
		// detection is ground-truth equivalent to the apply layer.
		if (
			effectiveTargetLocalId !== undefined &&
			findNodeByLocalIdAndApi(this.api, effectiveTargetLocalId) === undefined
		) {
			if (this.telemetryEventsEnabled) {
				this.emitToolCallResult({
					api: this.api,
					traceId: this.traceId,
					commandType: command.type,
					result: 'anomaly',
					symptom: 'local_id_not_found',
					targetLocalId: effectiveTargetLocalId,
				});
			}
			return { anomalyDetected: true, preApplyLocalIds };
		}
		return { anomalyDetected: false, preApplyLocalIds };
	}

	/**
	 * Apply-anomaly diagnostics: post-apply phase. Diffs the post-apply
	 * full-depth localId snapshot against the pre-apply snapshot (both
	 * built via `collectAllLocalIds`, so every depth is included) and
	 * fires:
	 * - `unexpected_node_removal` when nodes disappeared that the command
	 *   wasn't supposed to remove/relocate. Reports
	 *   `effectiveTargetLocalId` so the dashboard reader sees the localId
	 *   the apply path actually acted on.
	 * - `no_document_change` when a mutating command produced no document
	 *   change at all.
	 * Caches the post-apply snapshot for reuse by the next applyCommand.
	 *
	 * Caller is responsible for computing `effectiveTargetLocalId` so it
	 * reflects what the apply path actually acted on (see `runPreApplyDiagnostics`).
	 */
	private runPostApplyDiagnostics(
		command: StreamingCommand,
		preApplyLocalIds: Set<string>,
		effectiveTargetLocalId: string | undefined,
	): boolean {
		const postApplyDoc = getPmDoc(this.api);
		if (postApplyDoc === undefined) {
			// Editor view unavailable post-apply (teardown race). Skip
			// the diff — can't distinguish "everything removed" from
			// "couldn't observe post-apply state".
			return false;
		}
		// `unexpected_node_removal` is retired: `unexpected_content_removal`
		// from the post-apply outcome analyzer is the content-loss detector.
		// At gate cleanup, delete this block, `extractExplicitRemovalTargets`,
		// the `preApplyLocalIds` snapshot and the `removedLocalIds` attribute.
		if (!fg('platform_editor_ai_remove_node_removal_symptom')) {
			// Full-depth snapshot so the diff catches collateral removals
			// nested inside layouts, expand blocks, panels, table cells, etc.
			const postApplyLocalIds = collectAllLocalIds(postApplyDoc);

			// Pass `docBeforeStreaming` so the helper can expand each direct
			// target to include its descendant `localId`s. Required since
			// the diff above (`collectAllLocalIds`) is full-depth — removing
			// a container would otherwise spuriously fire
			// `unexpected_node_removal` for every nested descendant.
			const explicitTargets = extractExplicitRemovalTargets(
				command,
				this.docBeforeStreaming ?? undefined,
			);
			const removedLocalIds: string[] = [];
			preApplyLocalIds.forEach((id) => {
				if (!postApplyLocalIds.has(id) && !explicitTargets.has(id)) {
					removedLocalIds.push(id);
				}
			});
			if (
				removedLocalIds.length > 0 &&
				// Skip if the command already fired its own anomaly (e.g. setAttrs
				// via notifyInvalidAttrs) — one command should produce exactly one
				// toolCallResult event, not this plus unexpected_node_removal.
				!this.toolCallResultFiredForCurrentCommand
			) {
				if (this.telemetryEventsEnabled) {
					this.toolCallResultFiredForCurrentCommand = true;
					this.emitToolCallResult({
						api: this.api,
						traceId: this.traceId,
						commandType: command.type,
						result: 'anomaly',
						symptom: 'unexpected_node_removal',
						targetLocalId: effectiveTargetLocalId,
						removedLocalIds,
					});
				}
				return true;
			}
		}

		// Streamable commands (`replaceNode`, `replaceDoc`,
		// `insertNodeBefore`, `insertNodeAfter`, `appendNode`,
		// `appendNodeToEnd`) are two-phase: the apply call only sets a
		// selection (and sometimes inserts a placeholder paragraph), and
		// the actual content change arrives later via streaming chunks.
		// At apply time the document has legitimately not changed yet, so
		// firing `no_document_change` here would produce false positives
		// for every streamable command. The deferred check fires from
		// `checkAndFireNoDocumentChangeAnalytics` in `endExperiencePromise`
		// once all stream chunks have been applied.
		if (
			!isAStreamableCommand(command) &&
			this.docBeforeStreaming &&
			postApplyDoc.eq(this.docBeforeStreaming) &&
			// Skip if the command already fired its own anomaly (e.g. setAttrs
			// via notifyInvalidAttrs) — one failed command should produce exactly
			// one toolCallResult event, not this plus a generic no_document_change.
			!this.toolCallResultFiredForCurrentCommand
		) {
			if (this.telemetryEventsEnabled) {
				this.toolCallResultFiredForCurrentCommand = true;
				this.emitToolCallResult({
					api: this.api,
					traceId: this.traceId,
					commandType: command.type,
					result: 'anomaly',
					symptom: 'no_document_change',
				});
			}
			return true;
		}

		return false;
	}

	/**
	 * Resume-mode counterpart of {@link applyCommand}.
	 *
	 * Unlike `applyCommand`, it does NOT invoke a tool-call action (which would set
	 * the selection to end-of-doc and re-insert a placeholder, landing content in
	 * the wrong place). Instead it sets the selection to the stored, mapped
	 * positions via `reapply`, so the subsequent `openAIModal` (which reads its
	 * range from `state.selection` when `isExternal` is set) fills the original
	 * range.
	 *
	 * Kept as a dedicated method (rather than simply skipping `applyCommand`) so we
	 * have a single home for resume/reapply telemetry.
	 */
	private reapplyCommand(command: StreamingCommand, resume: { toolCallId: string }): boolean {
		this.currentCommand = command;
		this.docBeforeStreaming = getPmDoc(this.api);
		// Intentionally no tool-call action and no apply-anomaly diagnostics here:
		// resume re-applies into an explicit, already-resolved range. We set the
		// selection to the stored, transaction-mapped positions so that the
		// subsequent `openAIModal` (which reads its range from `state.selection`
		// when `isExternal` is set) re-applies the content where the aborted call
		// left off.
		//
		// The position is resolved at apply time from the toolCallId by reading the
		// LATEST (transaction-remapped) stored position; see `resolveResumePositions`
		// for the full rationale (multiple aborted tool calls resumed in sequence
		// must each anchor to their own remapped range).
		const resolved = resolveResumePositions(this.api, resume);
		if (!resolved) {
			// No stored position (shouldn't happen): re-run the command so content is
			// not lost. resumeAction already fires the fallback anomaly event.
			return this.applyCommand(command);
		}
		return this.reapply(resolved.fromPos, resolved.toPos);
	}

	private initStream(command: StreamingCommand, options: InitOptions) {
		const resume = options?.resume;
		// Resume mode: skip the tool-call action (which would re-resolve to end-of-doc)
		// and re-apply at the stored, mapped positions instead.
		const applied = resume ? this.reapplyCommand(command, resume) : this.applyCommand(command);
		// If we couldn't apply the command (ie. broken localId) add it to the abort cache so we don't apply anymore commands
		if (!applied) {
			// Never start the stream and resolve immediately
			if (this.currentAction?.id) {
				this.updateAbortedCache([this.currentAction?.id]);
			}
			return true;
		}

		// Must run before `openAIModal`, which moves the selection off the destination the
		// command just anchored to.
		this.currentParentNodes =
			fg('platform_editor_ai_streaming_panel_fix') &&
			SELECTION_ANCHORED_COMMAND_TYPES.has(command.type)
				? getParentNodeTypes(this.api)
				: undefined;

		this.streamFlowPacer?.reset();
		this.adfStreamer?.reset();
		const isCommit = resolveApplyMode(options) === 'commit';
		// Commit apply never pushes into the pacer. Starting it here would leave
		// `endExperiencePromise` waiting on `onComplete` that never fires.
		if (!isCommit) {
			// We have to set an empty callback before starting otherwise the flow pacer wont start. We need to cleanup the flow pacer
			// and make it more generic.
			this.streamFlowPacer?.setEmit(() => {});
			this.streamFlowPacer?.start();
			// Fire a simple operational event measuring the gap between the backend
			// finishing its stream and the pacer finishing draining it (HTML+ lag).
			this.streamFlowPacer?.onPacerCompletionGap((info) => {
				firePacerCompletionGapAnalytics({
					api: this.api,
					backendEndToPacerCompleteMs: info.backendEndToPacerCompleteMs,
					backlogCharsAtBackendEnd: info.backlogCharsAtBackendEnd,
					charsPerTickAtComplete: info.charsPerTickAtComplete,
					terminalType: info.terminalType,
					traceId: this.traceId,
				});
			});
		}
		const isCreate =
			(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
			command.streamingType === 'create';
		let tableReplacementRange: { overrideFromPos: number; overrideToPos: number } | undefined;
		if (
			!resume &&
			command.type === 'replaceNode' &&
			isExperimentEnabled('platform_editor_ai_table_replace_range_fix')
		) {
			const doc = getPmDoc(this.api);
			const target = doc && findNodeByLocalIdAndDoc(doc, command.localId);
			if (target?.node.type.spec.tableRole === 'table') {
				// Table selection normalization can turn a whole-table NodeSelection
				// into a single CellSelection. Keep the command's exact node range so
				// streaming and commit apply replace the table, including its wrappers.
				tableReplacementRange = {
					overrideFromPos: target.pos,
					overrideToPos: target.pos + target.node.nodeSize,
				};
			}
		}
		this.api.aiExperience.actions.openAIModal({
			...tableReplacementRange,
			invokedFrom: 'streamingApi',
			configItem: UNSTABLE_configItems.freeGenerateAifcConfigItem,
			// Typewriter still paints the active write. Commit apply skips it so
			// unified chrome can own the post-apply treatment. Resume is
			// selection-based: it must keep the remapped range.
			withStreamingHighlight: !isCreate && !isCommit && !resume,
			skipSelectionHighlight: !resume && (isCommit || isCreate),
			// don't show accept reject screen for staging first-time creation streaming
			withAcceptReject: !isCreate,
			hideLoadingScreen: options?.hideLoadingScreen ?? false,
			...(options?.shouldExpandPositions === false ? { shouldExpandPositions: false } : {}),
			traceId: this.traceId,
			singleInstrumentationId: this.singleInstrumentationId,
			isCreate,
			promptVersion: this.promptVersion,
		});
		if (!isAStreamableCommand(command)) {
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: this.currentCommand,
				docBeforeStreaming: this.docBeforeStreaming,
				traceId: this.traceId,
				onToolCallResult: (args) => this.emitToolCallResult(args),
			});
		}
		return false;
	}

	private async initStreamPromise(command: StreamingCommand, options: InitOptions): Promise<void> {
		await new Promise<void>((resolve) => {
			if (this.initStream(command, options)) {
				resolve();
				return;
			}
			// Commit apply never mounts typewriter decorations, so `isRunning`
			// may never flip. Waiting here stalls the queue and drops later
			// tool calls / resume writes.
			if (resolveApplyMode(options) === 'commit') {
				resolve();
				return;
			}
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
				if (nextSharedState.aiDecorationExperiencePluginState.isRunning) {
					unsubscribe?.();
					resolve();
				}
			});
		});
	}

	public startSession(
		sessionId: string,
		traceId?: string,
		singleInstrumentationId?: string,
		promptVersion?: string,
	): void {
		this.traceId = traceId;
		fireSessionStartedAnalytics({ api: this.api, sessionId, traceId, promptVersion });
		this.singleInstrumentationId = singleInstrumentationId;
		this.promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
			? promptVersion
			: undefined;
		this.hasCommitApplyInSession = false;
		this.pendingCommitResultCancel?.();
		this.resetSessionOutcomeState();
		// Capture the doc as it was before any streaming command runs.
		// `analyzeOutcome` will diff this against the post-session doc
		// at end-of-session to compute structural-delta fields.
		this.preSessionDoc = getPmDoc(this.api);
	}

	public endSession(_sessionId: string): void {
		this.runPostApplyOutcomeAnalysis();
		this.fireSessionOutcomeOnce();
		if (this.hasCommitApplyInSession) {
			// Legacy ExperienceApplication (xstate off): keep the modal up so
			// accept/reject can mount (`accept-reject.spec.ts`).
			// SessionUI (xstate on): `endAIExperience()` lets Loading cleanup
			// end the experience instead of showing review (`session-ui.spec.ts`).
			if (!isAiXstateMigrationEnabled()) {
				this.isFinalStream = true;
			}
			this.endExperience();
			this.hasCommitApplyInSession = false;
		}
		this.traceId = undefined;
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
		this.currentCommand = undefined;
	}

	/**
	 * Fire the authoritative `sessionOutcome` analytics event for the current
	 * session. Idempotent — calling this multiple times within the same session
	 * results in at most a single event being fired.
	 *
	 * If `lifecycleSymptom` is supplied, it overrides any repair-derived
	 * symptom — a session that never reached end-of-stream cannot be a
	 * success. It resolves to `outcome_class: 'aborted'` when an
	 * `abortReason` is supplied and `'failure'` otherwise.
	 */
	private fireSessionOutcomeOnce(
		options: {
			abortReason?: SessionAbortReason;
			errorMessage?: string;
			lifecycleSymptom?: 'session_aborted' | 'session_timeout' | 'user_aborted';
		} = {},
	): void {
		if (this.sessionOutcomeFired) {
			return;
		}
		this.sessionOutcomeFired = true;
		const { lifecycleSymptom, abortReason, errorMessage } = options;

		const { anyJsonInvalid, anyAdfInvalid, unrepairedJsonCount, unrepairedAdfCount } =
			this.sessionRepairSignals;

		// Derive the session-level repair-signal contract reported on the
		// outcome event:
		// - `finalJsonInvalid` / `finalAdfInvalid`: did any action see that
		//   layer become invalid at all?
		// - `finalJsonRepaired` / `finalAdfRepaired`: was every invalid
		//   occurrence in the session recovered? (i.e. AND over the per-action
		//   `repaired` flags for actions where invalid=true). False when the
		//   layer was never invalid, matching the per-action semantic.
		const finalJsonInvalid = anyJsonInvalid;
		const finalAdfInvalid = anyAdfInvalid;
		const finalJsonRepaired = anyJsonInvalid && unrepairedJsonCount === 0;
		const finalAdfRepaired = anyAdfInvalid && unrepairedAdfCount === 0;

		// Post-apply analysis flags. Read from the analyzer result computed
		// in `runPostApplyOutcomeAnalysis`. Each flag drives one suspicious-
		// signal event AND (if the session would otherwise have succeeded)
		// upgrades the outcome class to `success_but_suspicious`.
		//
		// `noMeaningfulEffect` only counts when the session actually ran a
		// mutating tool call — a read-only session that happens to leave
		// the doc unchanged is not suspicious, it's expected.
		const analysis = this.lastOutcomeAnalysis;
		const hadMutatingToolCall = this.sessionToolCalls.some((tc) =>
			MUTATING_COMMAND_TYPES.has(tc.commandType),
		);
		const unintendedRegionRemoved = analysis !== undefined && analysis.unintendedRegionRemoved;
		const unintendedRegionModified = analysis !== undefined && analysis.unintendedRegionModified;
		// `textCharacterDelta` tolerance: we treat ±1 character as
		// negligible because the apply layer can legitimately inject or
		// strip a single trailing whitespace / newline / punctuation
		// character during ProseMirror normalisation (e.g. closing a block
		// node, collapsing adjacent whitespace, trimming a trailing space
		// the model emitted). Strict equality to 0 would generate
		// false-positive `noMeaningfulEffect` events for sessions whose
		// only effect was a one-character normalisation artefact. A wider
		// tolerance would risk hiding real (but tiny) edits.
		//
		// `attrSignaturesEqual` catches attribute-only edits (e.g. cell
		// background, panel type, heading level) that node count + text
		// length miss. See CCI-16816.
		// CCI-16968: when a replaceNode creates replacement nodes with fresh
		// localIds, the targeted node disappears from the post-doc top-level
		// snapshot. In that case the totalNodeDelta and textCharacterDelta
		// metrics are coincidentally 0/±1 even though real content was
		// rewritten — the content just moved to a new localId. Suppress
		// noMeaningfulEffect when any targeted top-level node is absent from
		// the post-doc (the replacement genuinely happened).
		const noMeaningfulEffect =
			analysis !== undefined &&
			hadMutatingToolCall &&
			analysis.totalNodeDelta === 0 &&
			Math.abs(analysis.textCharacterDelta) <= 1 &&
			analysis.attrSignaturesEqual &&
			!analysis.anyAffectedLocalIdDisappearedPostApply;
		const intentEffectMismatch = analysis !== undefined && analysis.intentEffectMismatch;

		// Outcome class derivation per the design doc (Phase 1 + 3):
		// - aborted                : the session was aborted and the caller
		//                            told us why. Checked first: the
		//                            inactivity abort reports `session_timeout`,
		//                            the same symptom the orchestrator's own
		//                            queue-stall reports, so the symptom alone
		//                            cannot distinguish the two. `abortReason`
		//                            can, and is only ever set on the bridge
		//                            abort path.
		// - failure                : any layer (ADF or JSON) was invalid and
		//                            could not be repaired, OR the session
		//                            ended via a lifecycle event (abort /
		//                            timeout) with no reason supplied
		// - success_with_repairs   : a layer was invalid but every invalid
		//                            occurrence was recovered
		// - clean_success          : no repair signals fired at all
		// - success_but_suspicious : the session would otherwise have been
		//                            clean_success or success_with_repairs
		//                            but the post-apply analyzer flagged
		//                            one or more suspicious signals
		// `failure` is never overridden by a suspicious signal — an
		// unrecovered apply error is more important than "looks fishy".
		const adfFailed = finalAdfInvalid && !finalAdfRepaired;
		const jsonFailed = finalJsonInvalid && !finalJsonRepaired;
		const anySuspiciousSignal =
			unintendedRegionRemoved ||
			unintendedRegionModified ||
			noMeaningfulEffect ||
			intentEffectMismatch;
		let outcomeClass: OutcomeClass;
		if (abortReason !== undefined) {
			outcomeClass = 'aborted';
		} else if (
			lifecycleSymptom !== undefined ||
			adfFailed ||
			jsonFailed ||
			this.sessionCommitRejection !== undefined ||
			this.sessionStructuralLoss ||
			this.sessionCellConversionRejected
		) {
			outcomeClass = 'failure';
		} else if (anySuspiciousSignal) {
			outcomeClass = 'success_but_suspicious';
		} else if (finalAdfRepaired || finalJsonRepaired) {
			outcomeClass = 'success_with_repairs';
		} else {
			outcomeClass = 'clean_success';
		}

		// Compute a small histogram of command types to help debug
		// no-effect sessions ("what was being attempted?"). Keys are
		// orchestrator command-type constants (not user content). Scoped
		// deliberately to `noMeaningfulEffect` sessions — avoids inflating
		// every sessionOutcome payload with histogram data that is only
		// useful when the session produced no meaningful change.
		let commandTypeCounts: Record<string, number> | undefined;
		if (noMeaningfulEffect) {
			commandTypeCounts = {};
			for (const tc of this.sessionToolCalls) {
				commandTypeCounts[tc.commandType] = (commandTypeCounts[tc.commandType] ?? 0) + 1;
			}
		}

		// True when any of the suspicious signals that fired came from a
		// heuristic rather than a backend-supplied label. Currently always
		// true for `unintendedRegionModified` (text-length proxy) and
		// `intentEffectMismatch` (intent inferred from tool-call mix).
		// Lets dashboards filter the noisier inferred signals out of
		// headline KPIs.
		const heuristicInferred = unintendedRegionModified || intentEffectMismatch ? true : undefined;

		// Pick the worst (symptom, detectionLayer) we observed: lifecycle >
		// unrecovered failure > successful repair > clean. `detectionLayer`
		// names where the symptom was *first surfaced* (parser for repair
		// signals, orchestrator for apply / lifecycle), not where this
		// event is fired. Unmapped pairs fall back to `unknown`/`low`.
		let detectionLayer: DetectionLayer = 'orchestrator';
		let symptom: string | undefined;
		if (lifecycleSymptom !== undefined) {
			// Lifecycle symptoms are orchestrator-owned (abort / timeout).
			detectionLayer = 'orchestrator';
			symptom = lifecycleSymptom;
		} else if (adfFailed) {
			// The orchestrator surfaced an unrecovered ADF schema failure
			// when it tried to apply the streamed ADF.
			detectionLayer = 'orchestrator';
			symptom = 'apply_failed_adf_schema_error';
		} else if (jsonFailed) {
			// Parser-layer detection. There is no dedicated routing-table
			// entry for an unrecovered JSON failure today; we still attach
			// the symptom so dashboards can see it, and it routes to
			// `unknown`/`low` until the routing table grows an entry
			// covering this case.
			detectionLayer = 'parser';
			symptom = 'final_json_invalid';
		} else if (this.sessionCommitRejection) {
			detectionLayer = 'orchestrator';
			symptom =
				this.sessionCommitRejection.reason === 'unhealthyFragment'
					? 'apply_failed_adf_schema_error'
					: 'apply_failed';
		} else if (this.sessionStructuralLoss) {
			detectionLayer = 'orchestrator';
			symptom = 'applied_structure_mismatch';
		} else if (this.sessionCellConversionRejected) {
			detectionLayer = 'orchestrator';
			symptom = 'apply_failed';
		} else if (finalAdfRepaired) {
			// ADF repair is the higher-severity success signal — checked
			// before JSON repair so a session that observed both still
			// surfaces the more impactful symptom (ADF/high) rather than
			// the weaker JSON/medium one. Preserves the "worst observed
			// signal wins" invariant.
			detectionLayer = 'parser';
			symptom = 'final_adf_invalid';
		} else if (finalJsonRepaired) {
			detectionLayer = 'parser';
			symptom = 'final_json_repaired';
		} else if (anySuspiciousSignal) {
			// Pick the highest-severity suspicious signal observed — worst
			// signal wins, matching the same invariant used for repair/failure
			// signals above. The four signals are checked in severity order:
			// unintendedRegionRemoved (content disappeared) > unintendedRegionModified
			// (content changed unexpectedly) > noMeaningfulEffect (no net change) >
			// intentEffectMismatch (structural delta contradicts inferred intent).
			detectionLayer = 'post_apply';
			if (unintendedRegionRemoved) {
				symptom = 'unexpected_content_removal';
			} else if (unintendedRegionModified) {
				symptom = 'unintended_text_modification';
			} else if (noMeaningfulEffect) {
				symptom = 'no_meaningful_effect';
			} else {
				// intentEffectMismatch
				symptom = 'semantic_mismatch';
			}
		}

		if (!this.telemetryEventsEnabled) {
			return;
		}
		fireSessionOutcomeAnalytics({
			api: this.api,
			traceId: this.traceId,
			outcomeClass,
			detectionLayer,
			symptom,
			abortReason,
			errorMessage,
			finalJsonInvalid,
			finalJsonRepaired,
			finalAdfInvalid,
			finalAdfRepaired,
			commitRejection: this.sessionCommitRejection,
			// Forward the post-apply analyzer flags so dashboards can see
			// "what happened" and "why it was suspicious" on a single
			// event. Each is `undefined` when the analyzer didn't run
			// (e.g. session aborted before any apply).
			...(analysis !== undefined
				? {
						unintendedRegionRemoved,
						unintendedRegionModified,
						noMeaningfulEffect,
						intentEffectMismatch,
						// Raw deltas — only attached when the corresponding
						// suspicious flag is true, to avoid forwarding
						// meaningless zeros on clean sessions.
						...(unintendedRegionRemoved
							? {
									removedNodeCount: analysis.removedNodeCount,
									totalNodeDelta: analysis.totalNodeDelta,
									// Indicates whether unintendedRegionRemoved used
									// backend-supplied selectionLocalIds (precise) or
									// the proxy fallback. Backend plumbing arrives with
									// CCI-15978; until then always false.
									selectionLocalIdsAvailable: false,
								}
							: {}),
						...(unintendedRegionModified
							? { textCharacterDelta: analysis.textCharacterDelta }
							: {}),
					}
				: {}),
			...(commandTypeCounts !== undefined ? { commandTypeCounts } : {}),
			...(isExperimentEnabled('platform_editor_ai_session_anomaly_counts_exp')
				? { anomalyCounts: { ...this.sessionAnomalyCounts } }
				: {}),
			...(heuristicInferred !== undefined ? { heuristicInferred } : {}),
		});
	}

	/**
	 * Schedules the `no_document_change` toolCallAnomaly check for streamable commands.
	 *
	 * The legacy path subscribes to shared state and compares only after streaming ends
	 * so it does not read the document before deferred finalization.
	 * Commit-outcome observation subscribes before COMPLETE and waits for the matching
	 * result. A receipt-less completion settles only after `isStreaming` is false and
	 * the stream phase is explicitly `finalizing` or `idle`; a missing phase or active
	 * streaming phase can still produce a receipt and must remain pending. A terminal
	 * error releases the waiter without running the document comparator.
	 *
	 * The waiter distinguishes a reported result, terminal error, timeout, and lifecycle
	 * cancellation. Timeout tears down the captured action synchronously before the
	 * queue advances. Abort, destroy, and session replacement cancel the stale waiter
	 * without letting its continuation clean up a later action.
	 *
	 * No-op when:
	 * - `aiExperience` plugin is absent (streaming state machine unavailable).
	 */
	private scheduleDeferredNoDocChangeAnalytics(
		observeCommit = false,
		onCommitTimeout?: () => void,
	):
		| {
				afterCompleteDispatch: () => void;
				completed: Promise<CommitResultWaitOutcome> | undefined;
		  }
		| undefined {
		if (
			!this.telemetryEventsEnabled ||
			!this.api.aiExperience ||
			(this.toolCallResultFiredForCurrentCommand && !observeCommit)
		) {
			return;
		}
		const capturedCommand = this.currentCommand;
		const capturedAction = this.currentAction;
		const capturedActionId = capturedAction?.id;
		if (observeCommit && capturedActionId === undefined) {
			return;
		}
		const capturedCommandResultId = this.currentCommandResultId;
		const capturedDocBeforeStreaming = this.docBeforeStreaming;
		const capturedTraceId = this.traceId;
		// Keep late callbacks attached to the session that scheduled them.
		const capturedAnomalyCounts = this.sessionAnomalyCounts;
		const requestedReplacement = this.finalRequestedReplacement;
		const capturedTargetRole = this.currentAction?.initialNode?.type.spec.tableRole;
		const capturedCellKindConversionEnabled =
			capturedCommand?.type === 'replaceNode' &&
			(capturedTargetRole === 'cell' || capturedTargetRole === 'header_cell') &&
			isExperimentEnabled('platform_editor_ai_replace_cell_kind');
		const verifyStructure = capturedCommand?.type === 'replaceNode' && this.shouldVerifyStructure();
		let capturedResultReported = false;
		let terminalEventDispatched = !observeCommit;
		let resolveResult: ((outcome: CommitResultWaitOutcome) => void) | undefined;
		const resultComplete = observeCommit
			? new Promise<CommitResultWaitOutcome>((resolve) => {
					resolveResult = resolve;
				})
			: undefined;
		const reportResult = () => {
			if (capturedResultReported) {
				return;
			}
			const commitResult = observeCommit
				? this.api.aiExperience?.sharedState.currentState()?.aiSessionPluginState?.commitResult
				: undefined;
			const commitOutcome =
				commitResult?.actionId === capturedActionId ? commitResult?.outcome : undefined;
			if (commitOutcome && !commitOutcome.applied) {
				if (!this.sessionCommitRejection || commitOutcome.reason === 'unhealthyFragment') {
					this.sessionCommitRejection = commitOutcome;
				}
				capturedResultReported = true;
				if (
					capturedCommand &&
					!(
						this.currentCommandResultId === capturedCommandResultId &&
						this.toolCallResultFiredForCurrentCommand
					)
				) {
					if (this.currentCommandResultId === capturedCommandResultId) {
						this.toolCallResultFiredForCurrentCommand = true;
					}
					fireToolCallResult({
						api: this.api,
						traceId: capturedTraceId,
						commandType: capturedCommand.type,
						result: 'anomaly',
						symptom:
							commitOutcome.reason === 'unhealthyFragment'
								? 'apply_failed_adf_schema_error'
								: 'apply_failed',
						commitRejection: commitOutcome,
						...(commitOutcome.reason === 'cellConversionRejected' && {
							errorMessage: commitOutcome.rejection.message,
						}),
					});
				}
				resolveResult?.('reported');
				return;
			}
			if (
				capturedResultReported ||
				(this.currentCommandResultId === capturedCommandResultId &&
					this.toolCallResultFiredForCurrentCommand)
			) {
				resolveResult?.('reported');
				return;
			}
			if (observeCommit) {
				capturedResultReported = true;
				resolveResult?.('reported');
				if (this.currentCommandResultId === capturedCommandResultId) {
					this.toolCallResultFiredForCurrentCommand = true;
				}
			}
			const cellRejection = capturedCellKindConversionEnabled
				? this.api.aiExperience?.sharedState.currentState()?.aiSessionPluginState
						?.cellKindConversionRejection
				: undefined;
			if (cellRejection && capturedCommand?.type === 'replaceNode') {
				this.sessionCellConversionRejected = true;
				capturedResultReported = true;
				if (this.currentCommandResultId === capturedCommandResultId) {
					this.toolCallResultFiredForCurrentCommand = true;
				}
				this.emitToolCallResult(
					{
						api: this.api,
						traceId: capturedTraceId,
						commandType: capturedCommand.type,
						result: 'anomaly',
						symptom: 'apply_failed',
						errorMessage: cellRejection.message,
					},
					capturedAnomalyCounts,
				);
				return;
			}
			const appliedDoc = verifyStructure ? getPmDoc(this.api) : undefined;
			const structureComparison =
				verifyStructure &&
				capturedCommand?.type === 'replaceNode' &&
				capturedDocBeforeStreaming &&
				appliedDoc &&
				requestedReplacement !== undefined
					? compareReplacementStructure(
							capturedDocBeforeStreaming,
							appliedDoc,
							capturedCommand.localId,
							requestedReplacement,
						)
					: undefined;
			const structureVerified = verifyStructure ? structureComparison !== undefined : undefined;
			if (structureComparison?.mismatch && capturedCommand?.type === 'replaceNode') {
				this.sessionStructuralLoss = true;
				capturedResultReported = true;
				if (this.currentCommandResultId === capturedCommandResultId) {
					this.toolCallResultFiredForCurrentCommand = true;
				}
				this.emitToolCallResult(
					{
						api: this.api,
						traceId: capturedTraceId,
						commandType: capturedCommand.type,
						result: 'anomaly',
						symptom: 'applied_structure_mismatch',
						structureVerified,
						expectedStructure: structureComparison.expectedStructure,
						actualStructure: structureComparison.actualStructure,
						expectedNodeType: structureComparison.expectedNodeType,
						actualNodeType: structureComparison.actualNodeType,
						firstDifferenceAttribute: structureComparison.firstDifferenceAttribute,
						firstDifferenceKind: structureComparison.firstDifferenceKind,
						firstDifferencePath: structureComparison.firstDifferencePath,
						structureTruncated: structureComparison.structureTruncated,
						comparisonScope: structureComparison.comparisonScope,
					},
					capturedAnomalyCounts,
				);
				return;
			}
			if (verifyStructure) {
				capturedResultReported = true;
				if (this.currentCommandResultId === capturedCommandResultId) {
					this.toolCallResultFiredForCurrentCommand = true;
				}
			}
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: capturedCommand,
				docBeforeStreaming: capturedDocBeforeStreaming,
				traceId: capturedTraceId,
				onToolCallResult: (args) => this.emitToolCallResult(args, capturedAnomalyCounts),
				structureVerified,
			});
		};
		this.pendingNoDocChangeUnsubscribe?.();
		const cancelCommitResult = (reason: 'timeout' | 'lifecycle' = 'lifecycle') => {
			capturedResultReported = true;
			stopListening();
			if (reason === 'timeout') {
				try {
					onCommitTimeout?.();
				} catch (error) {
					try {
						logException(error instanceof Error ? error : new Error(String(error)), {
							location: 'editor-ai-streaming-commit-timeout-cleanup',
						});
					} catch {
						// A cleanup or reporting failure must not keep the queue's timeout pending.
					}
				} finally {
					resolveResult?.('timed_out');
				}
				return;
			}
			resolveResult?.('lifecycle_cancelled');
		};
		const stopListening = () => {
			unsubscribe?.();
			if (
				this.pendingNoDocChangeUnsubscribe === (observeCommit ? cancelCommitResult : unsubscribe)
			) {
				this.pendingNoDocChangeUnsubscribe = undefined;
			}
			if (this.pendingCommitResultCancel === cancelCommitResult) {
				this.pendingCommitResultCancel = undefined;
			}
		};
		const reportIfReady = (
			sharedState: ReturnType<API['aiExperience']['sharedState']['currentState']>,
		) => {
			if (capturedResultReported || !sharedState || sharedState.isStreaming) {
				return;
			}
			const session = sharedState.aiSessionPluginState;
			const hasMatchingResult = session?.commitResult?.actionId === capturedActionId;
			if (observeCommit) {
				if (hasMatchingResult) {
					stopListening();
					reportResult();
					return;
				}
				if (!terminalEventDispatched) {
					return;
				}
				if (session?.status === 'error') {
					capturedResultReported = true;
					stopListening();
					resolveResult?.('terminal_error');
					return;
				}
				const streamPhase = sharedState.streamingState?.streamPhase;
				if (
					session?.status !== 'complete' ||
					(streamPhase !== 'finalizing' && streamPhase !== 'idle')
				) {
					return;
				}
			}
			stopListening();
			reportResult();
		};
		const unsubscribe = this.api.aiExperience.sharedState.onChange(({ nextSharedState }) => {
			if (capturedResultReported) {
				return;
			}
			reportIfReady(nextSharedState);
		});
		this.pendingNoDocChangeUnsubscribe = observeCommit ? cancelCommitResult : unsubscribe;
		this.pendingCommitResultCancel = observeCommit ? cancelCommitResult : undefined;
		// Recheck after subscribing so a terminal state cannot be missed.
		if (observeCommit || capturedCellKindConversionEnabled) {
			const currentState = this.api.aiExperience.sharedState.currentState();
			if (observeCommit) {
				reportIfReady(currentState);
			} else if (
				currentState &&
				!currentState.isStreaming &&
				currentState.aiSessionPluginState?.cellKindConversionRejection
			) {
				stopListening();
				reportResult();
			}
		}
		return {
			completed: resultComplete,
			afterCompleteDispatch: () => {
				terminalEventDispatched = true;
				reportIfReady(this.api.aiExperience.sharedState.currentState());
			},
		};
	}

	/**
	 * Reset session-outcome bookkeeping so the next session starts from a clean
	 * slate. Called from `startSession`, `destroy`, and `abort`.
	 */
	private resetSessionOutcomeState(): void {
		this.sessionCommitRejection = undefined;
		this.sessionStructuralLoss = false;
		this.sessionCellConversionRejected = false;
		this.finalRequestedReplacement = undefined;
		this.cellKindConversionIntent = undefined;
		this.sessionRepairSignals = {
			anyJsonInvalid: false,
			anyAdfInvalid: false,
			unrepairedJsonCount: 0,
			unrepairedAdfCount: 0,
		};
		this.sessionOutcomeFired = false;
		this.sessionAnomalyCounts = {};
		// Post-apply outcome analyzer state. Cleared so the next session
		// starts with an empty tool-call record and no stale analysis.
		this.sessionToolCalls = [];
		this.lastOutcomeAnalysis = undefined;
		this.preSessionDoc = undefined;
	}

	/**
	 * Compute the structural-delta analysis for the just-completed session
	 * and stash it on `lastOutcomeAnalysis`. Called from `endSession`
	 * (gated). `fireSessionOutcomeOnce` reads `lastOutcomeAnalysis` to
	 * derive the `success_but_suspicious` outcome class and to fire the
	 * three suspicious-signal session-scope events.
	 *
	 * Skips silently if either the pre-session or post-session doc is
	 * unavailable (e.g. editor view torn down mid-stream) — partial-data
	 * analysis would produce misleading deltas.
	 */
	private runPostApplyOutcomeAnalysis(): void {
		const postDoc = getPmDoc(this.api);
		if (this.preSessionDoc === undefined || postDoc === undefined) {
			return;
		}
		this.lastOutcomeAnalysis = analyzeOutcome({
			preSessionAdf: this.preSessionDoc.toJSON() as DocLike,
			postSessionAdf: postDoc.toJSON() as DocLike,
			toolCalls: this.sessionToolCalls,
		});
	}

	public destroy(): void {
		this.queue = [];
		this.adfStreamer = undefined;
		this.currentCommand = undefined;
		this.currentParentNodes = undefined;
		this.docBeforeStreaming = undefined;
		this.traceId = undefined;
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
		this.hasCommitApplyInSession = false;
		this.pendingNoDocChangeUnsubscribe?.();
		this.pendingNoDocChangeUnsubscribe = undefined;
		this.resetSessionOutcomeState();
	}

	/**
	 * Classify an abort into the session-outcome fields to report for it.
	 *
	 * Behind `platform_editor_ai_telemetry_improvements`. With the gate off
	 * every abort collapses to `session_aborted` with no reason — byte-for-byte
	 * the pre-gate behaviour — which also means `abortReason` is never set and
	 * `outcome_class` can never become `aborted`. With the gate on the reason
	 * supplied by the bridge selects the symptom, so user cancels stop counting
	 * against the failure rate.
	 */
	private resolveAbortOutcome(options?: AbortSessionOptions): {
		abortReason?: SessionAbortReason;
		errorMessage?: string;
		lifecycleSymptom: 'session_aborted' | 'session_timeout' | 'user_aborted';
	} {
		if (!fg('platform_editor_ai_telemetry_improvements')) {
			return { lifecycleSymptom: 'session_aborted' };
		}

		const abortReason = options?.reason ?? 'unknown';
		switch (abortReason) {
			case 'user_abort':
				return { lifecycleSymptom: 'user_aborted', abortReason };
			case 'timeout':
				// Reuses the existing `session_timeout` symptom rather than minting
				// a new one; `abortReason` is what separates this from the
				// orchestrator's own queue-stall timeout.
				return { lifecycleSymptom: 'session_timeout', abortReason };
			case 'aborted_due_to_error':
				return {
					lifecycleSymptom: 'session_aborted',
					abortReason,
					errorMessage: options?.errorMessage,
				};
			case 'unknown':
			default:
				return { lifecycleSymptom: 'session_aborted', abortReason };
		}
	}

	private isAborted = false;
	/**
	 * Abort the current streaming and end the experience
	 * This is a hard abort and will clear the queue
	 * This will only restart the experience when initStreamCommand is called again
	 */
	public abort(options?: AbortSessionOptions): void {
		// Fire BEFORE any state teardown so `traceId` and
		// `singleInstrumentationId` are still set and the event can be
		// correlated in dashboards (matches the timeout / clean-drain paths).
		// We deliberately do NOT reset `sessionOutcomeFired` here — the
		// guard must persist through any follow-up `endSession` / timeout
		// for this same session; the next `startSession()` resets it.
		this.fireSessionOutcomeOnce(this.resolveAbortOutcome(options));
		this.pendingNoDocChangeUnsubscribe?.();
		this.pendingNoDocChangeUnsubscribe = undefined;
		const currentQueue = this.queue.map((cmd) => cmd.actionId);
		this.queue = [];

		// Add new aborted action IDs to end of array
		this.updateAbortedCache([...new Set(currentQueue), ...this.inProcessing]);
		this.isAborted = true;
		this.hasCommitApplyInSession = false;
		this.endExperience();
		this.isPendingResolveQueue = false;
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.currentAction = null;
		this.isFinalStream = false;
		this.currentCommand = undefined;
		this.traceId = undefined;
		this.singleInstrumentationId = undefined;
	}

	private updateAbortedCache(actionIds: string[]) {
		this.aborted = [...new Set([...this.aborted, ...actionIds])];
		const maxAbortedCacheLength = 5;
		if (this.aborted.length > maxAbortedCacheLength) {
			this.aborted = this.aborted.slice(-maxAbortedCacheLength);
		}
	}
}
